"use client";

/**
 * Meta Pixel — the browser half of the conversion feedback loop.
 *
 * Pairs with the server-side Conversions API (src/lib/meta/capi.ts): the Pixel
 * sets the _fbc / _fbp cookies and fires PageView + a browser CompleteRegistration
 * that shares its event_id with the CAPI event, so Meta dedupes the redundant
 * pair and match quality improves.
 *
 * 🚨 CONSENT: the Pixel is a NON-ESSENTIAL cookie under UK PECR, so it must not
 * load without marketing consent. It stays dark until BOTH a pixel id is
 * configured (NEXT_PUBLIC_META_PIXEL_ID, prod-only today) AND the shared
 * `.mucka.ai` consent cookie says granted (src/lib/consent/marketing-consent.ts).
 * It loads the moment someone says yes, not only on the next page load —
 * otherwise a visitor who accepts on the sign-up page is never seen signing up.
 *
 * Rendered by the sign-up page only, on the web only. The logged-in app runs no
 * ad pixel (its URLs carry client and job ids), and the native app would need
 * Apple's tracking prompt first.
 */

import { useEffect } from "react";
import { hasMarketingConsent, onMarketingConsentChange } from "@/lib/consent/marketing-consent";

const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

declare global {
  interface Window {
    // The queue/callMethod internals are only ever touched inside the any-typed
    // bootstrap below, so the public type is just the callable.
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

/**
 * Fire the browser-side CompleteRegistration with the SAME event_id the server
 * CAPI uses, so Meta dedupes the redundant pair. A safe no-op when the Pixel
 * never loaded (no id / no consent), which is the common case today.
 */
export function trackSignupComplete(eventId: string, valuePence: number): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq(
    "track",
    "CompleteRegistration",
    { value: valuePence / 100, currency: "GBP" },
    { eventID: eventId },
  );
}

/** Bootstrap the Pixel. Callers have already checked the id and the consent. */
function loadPixel(pixelId: string): void {
  if (window.fbq) return; // already initialised

  // Standard Meta Pixel bootstrap, inlined so nothing loads (and no cookie is
  // set) for a visitor who hasn't opted in — the script tag below is only
  // ever created once the caller has checked consent.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function (...args: unknown[]) {
      // `n.callMethod(...args)` keeps `this === n`, same as the snippet's
      // original `.apply(n, arguments)` — rewritten to satisfy prefer-spread /
      // prefer-rest-params without changing behaviour.
      if (n.callMethod) n.callMethod(...args);
      else n.queue.push(args);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // The bootstrap above sets window.fbq via an any-typed closure, which TS's
  // control-flow can't see — it still thinks the early `if (window.fbq) return`
  // narrowed it to undefined — so read it back through an explicit type.
  const fbq = window.fbq as ((...args: unknown[]) => void) | undefined;
  fbq?.("init", pixelId);
  fbq?.("track", "PageView");
}

export default function MetaPixel() {
  useEffect(() => {
    if (!PIXEL_ID) return;
    const pixelId = PIXEL_ID;
    if (hasMarketingConsent()) loadPixel(pixelId);
    return onMarketingConsentChange((value) => {
      if (value === "granted") {
        if (window.fbq) window.fbq("consent", "grant");
        else loadPixel(pixelId);
      } else if (window.fbq) {
        // A script can't be unloaded; revoke stops it sending anything further.
        window.fbq("consent", "revoke");
      }
    });
  }, []);

  return null;
}
